// Rolling back one optimistic write in a list cached by id (items, recipes,
// planned meals, templates). Pure, no React.

/** One row as it was before an optimistic write: `before` undefined means it wasn't in the list. */
export interface RowSnapshot<T> {
  before: T | undefined
  index: number
}

/** Takes the row at `id` and its place in `rows`, for revertRow. */
export function snapshotRow<T extends { id: string }>(rows: T[], id: string): RowSnapshot<T> {
  const index = rows.findIndex(r => r.id === id)
  return { before: index < 0 ? undefined : rows[index], index }
}

/**
 * A write failed: put back its one row and leave the rest as it is now. Never
 * the whole list as it was — another phone's changes may have arrived since,
 * and a list-wide rollback undid every one of them (the "ticks come back" bug).
 */
export function revertRow<T extends { id: string }>(rows: T[], id: string, snap: RowSnapshot<T>): T[] {
  const rest = rows.filter(r => r.id !== id)
  if (!snap.before) return rest
  if (rest.length < rows.length) return rows.map(r => (r.id === id ? snap.before! : r))
  const next = [...rest]
  next.splice(Math.max(0, Math.min(snap.index, next.length)), 0, snap.before)
  return next
}

/** snapshotRow for every id a write touches. */
export function snapshotRows<T extends { id: string }>(rows: T[], ids: string[]): Map<string, RowSnapshot<T>> {
  return new Map(ids.map(id => [id, snapshotRow(rows, id)]))
}

/** revertRow for each snapshot. */
export function revertRows<T extends { id: string }>(rows: T[], snaps: Map<string, RowSnapshot<T>>): T[] {
  let next = rows
  for (const [id, snap] of snaps) next = revertRow(next, id, snap)
  return next
}
